// The slate's day, as it lives in the URL: /league/NCAAF?date=2026-09-19
//
// It used to be React state on App (`selectedDate`), so a browser reload always
// landed on today and a slate could not be linked to or bookmarked. The URL is
// now the only source of truth for which day a slate shows.

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (n) => String(n).padStart(2, '0');

// A Date -> its LOCAL calendar day as YYYY-MM-DD.
export const toSlateDateString = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const todaySlateDate = () => toSlateDateString(new Date());

// YYYY-MM-DD -> a Date at LOCAL midnight, or null if it is not a real day.
//
// Never `new Date('2026-09-19')`: a date-only ISO string parses as UTC midnight,
// which in every US timezone is the PREVIOUS local day, so Next/Previous would
// step from the wrong day and the page would label itself with the wrong date.
// The round-trip check rejects rollovers like 2026-02-31 -> March 3.
export const parseSlateDate = (value) => {
  if (typeof value !== 'string') return null;
  const m = DATE_RE.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(y, mo - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
};

// The slate day `days` away from `value` (or from today if `value` is not a day).
export const shiftSlateDate = (value, days) => {
  const base = parseSlateDate(value) || new Date();
  return toSlateDateString(new Date(base.getFullYear(), base.getMonth(), base.getDate() + days));
};

// The Pacific calendar day of a timestamp. The backend's league-day window is
// Pacific midnight to Pacific midnight for every league (bet/util.py), so this is
// the slate a fixture actually appears on -- which a browser-local date is not for
// a late Pacific kickoff viewed from the East Coast.
export const pacificSlateDate = (isoTime) => {
  const t = new Date(isoTime);
  if (Number.isNaN(t.getTime())) return null;
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(t);
};

export const slateHref = (league, value) =>
  value ? `/league/${league}?date=${value}` : `/league/${league}`;
